/*
 * This function will group a list of objects by today, yesterday, last 7 days, and months
 */
import { groupBy } from "lodash";

export enum DateGroup {
  Today = "Today",
  Yesterday = "Yesterday",
  Last7Days = "Last 7 days",
}

export type DateGroupType =
  | "Today"
  | "Yesterday"
  | "Last 7 days"
  | (string & NonNullable<unknown>);

export function groupObjectByDate<T, R extends { [key in DateGroupType]: T[] }>(
  listObject: T[],
  dateField = "updatedAt",
): { group: DateGroupType; items: T[] }[] {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastSevenDays = new Date(today);
  lastSevenDays.setDate(lastSevenDays.getDate() - 7);

  const grouped = groupBy(listObject, (item: any) => {
    const itemDate = new Date(Reflect.get(item, dateField));
    if (itemDate.toDateString() === today.toDateString()) {
      return DateGroup.Today;
    } else if (itemDate.toDateString() === yesterday.toDateString()) {
      return DateGroup.Yesterday;
    } else if (itemDate > lastSevenDays) {
      return DateGroup.Last7Days;
    } else {
      return `${itemDate.toLocaleString("default", { month: "long" })} ${itemDate.getFullYear()}`;
    }
  }) as R;

  const sortedMonthGroups = Object.entries(grouped)
    .filter(
      ([key]) =>
        ![DateGroup.Today, DateGroup.Yesterday, DateGroup.Last7Days].includes(
          key as any,
        ),
    )
    .sort(([a], [b]) => {
      // a and b like March 2024, May 2024
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB.getTime() - dateA.getTime();
    })
    .map(([key, value]) => ({ group: key, items: value }));

  return [
    { group: DateGroup.Today, items: grouped[DateGroup.Today] || [] },
    { group: DateGroup.Yesterday, items: grouped[DateGroup.Yesterday] || [] },
    { group: DateGroup.Last7Days, items: grouped[DateGroup.Last7Days] || [] },
    ...sortedMonthGroups,
  ].filter((group) => group.items.length > 0);
}
